/**
 * Банер (UX-13; components.md, розд. 10): стан, що триває, — у межах області, якої стосується.
 * В області видно не більше одного банера: найважливіший за `priority` (менше — важливіше), решта
 * чекає, доки він зникне. Вміст банера — звичайні діти React на своєму місці; сервіс вирішує лише,
 * чия черга. Банери модулів поза деревом (`showBanner`) малює `<BannerSlot>` тієї ж області.
 *
 * `role`: `status` — за замовчуванням, `alert` — для `danger` (конфлікт, втрата даних).
 */
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { Icon, type IconName } from '../Icon.tsx';
import {
  announce,
  bannerData,
  hideBanner,
  registerBanner,
  retractAnnouncement,
  subscribe,
  topBanner,
} from './store.ts';

type Tone = 'info' | 'warn' | 'danger';

const ICON: Record<Tone, IconName> = { info: 'bell', warn: 'warning', danger: 'alert-circle' };

/**
 * Чи зараз черга цього банера в області: реєстрація — поки змонтований і `active`. До малювання
 * кадру (`useLayoutEffect`): інакше банер з'являється кадром пізніше і зсуває вміст під собою.
 */
function useBannerTurn(area: string, priority: number, active: boolean, key: string): boolean {
  useLayoutEffect(() => {
    if (!active) return;
    registerBanner(key, { area, priority });
    return () => hideBanner(key);
  }, [area, priority, active, key]);
  const top = useSyncExternalStore(
    subscribe,
    () => topBanner(area),
    () => null,
  );
  return active && top === key;
}

interface BannerProps {
  area: string;
  priority: number;
  tone?: Tone;
  /** Показ залежить від умови: так реєстрація не зникає й не з'являється разом з компонентом. */
  when?: boolean;
  icon?: IconName | false;
  /** Кнопки й посилання праворуч. */
  actions?: ReactNode;
  className?: string;
  testId?: string;
  /** Ключ банера: два однакові ключі — один банер (той самий банер не множиться). */
  bannerKey?: string;
  children: ReactNode;
}

export function Banner({
  area,
  priority,
  tone = 'info',
  when = true,
  icon,
  actions,
  className,
  testId,
  bannerKey,
  children,
}: BannerProps) {
  const ownId = useId();
  const key = bannerKey ?? ownId;
  const visible = useBannerTurn(area, priority, when, key);
  const textRef = useRef<HTMLDivElement>(null);
  useAnnounceOnShow(key, visible, tone, () => textRef.current?.textContent ?? undefined);
  if (!visible) return null;
  const iconName = icon === false ? null : (icon ?? ICON[tone]);
  return (
    <div
      className={`banner${tone === 'info' ? '' : ` banner--${tone}`}${className ? ` ${className}` : ''}`}
      role={tone === 'danger' ? 'alert' : 'status'}
      data-testid={testId}
    >
      {iconName && <Icon name={iconName} />}
      <div className="banner__text" ref={textRef}>
        {children}
      </div>
      {actions && <div className="banner__actions">{actions}</div>}
    </div>
  );
}

/**
 * Оголосити появу банера: `role="status"`, вставлений разом з текстом, програми читання часто
 * пропускають. `danger` (`role="alert"`) читається і так.
 */
function useAnnounceOnShow(
  id: string | null,
  visible: boolean,
  tone: Tone,
  text: () => string | undefined,
) {
  const read = useRef(text);
  read.current = text;
  // biome-ignore lint/correctness/useExhaustiveDependencies: id — новий банер у тому самому місці теж оголошуємо
  useEffect(() => {
    if (!visible || tone === 'danger') return;
    const value = read.current()?.trim();
    if (!value) return;
    announce(value);
    return () => retractAnnouncement(value);
  }, [id, visible, tone]);
}

/** Місце для банерів `showBanner` області: показує свій, лише коли його черга. */
export function BannerSlot({ area, className }: { area: string; className?: string }) {
  const top = useSyncExternalStore(
    subscribe,
    () => topBanner(area),
    () => null,
  );
  const data = top ? bannerData(top) : undefined;
  const tone = data?.tone ?? 'info';
  useAnnounceOnShow(top, Boolean(data), tone, () => data?.text);
  if (!data) return null;
  return (
    <div
      className={`banner${tone === 'info' ? '' : ` banner--${tone}`}${className ? ` ${className}` : ''}`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon name={ICON[tone]} />
      <span>{data.text}</span>
      {data.actions && data.actions.length > 0 && (
        <div className="banner__actions">
          {data.actions.map((a) => (
            <button key={a.label} className="link-btn" type="button" onClick={a.onAction}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
